import React, { useCallback, useMemo, useState } from "react";

// import { BasePlayerCardProps, usePlayerCardBase } from "../PlayerCardBase";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import KanbanPlayerCardView from "./KanbanPlayerCardView";
import { useRankReorderSortable } from "./rankReorderContext";

import { useActiveDragId } from "@/features/player-kanban/contexts/ActiveDragContext";

import { KANBAN_COLORS_DARK } from "@/features/player-kanban/constants/colors";
import { useCardEditorName } from "@/features/player-kanban/hooks/useCardEditorName";
import {
  BasePlayerCardProps,
  usePlayerCardBase,
} from "@/hooks/usePlayerCardBase";
import { RADIUS } from "@/config/radius";

/**
 * How visible the card left behind at the drag's origin stays while the card is
 * in flight (SE-6554, Figma 80:16392 — "Origin card stays in place at 40%
 * opacity until drop commits").
 *
 * A runtime opacity rather than a Tailwind class because it applies to the
 * dnd-kit drag node, whose style object is already computed per-render from the
 * transform; there is no static class to attach it to.
 */
const DRAGGING_ORIGIN_OPACITY = 0.4;

/**
 * Props interface for KanbanPlayerCard component
 * @remarks
 * Extends BasePlayerCardProps with Kanban-specific properties
 */
export interface KanbanPlayerCardProps extends BasePlayerCardProps {
  canEdit?: boolean;
  /**
   * SE-4684: Column-scoped 1-based rank shown as a subtle badge on the card.
   * Undefined outside the Big Board (no badge rendered).
   */
  rank?: number;
  /**
   * SE-5497: whole days the card has spent in its current stage. PIPELINE-only
   * (undefined on roster/watchlist, where the day-count row is not rendered).
   */
  daysInStage?: number | null;
  /** SE-5497: whether the card is over its stage's configured time target. */
  isStale?: boolean;
  /**
   * SE-5492: canonical player image URL for the avatar.
   * @remarks
   * Null for EVERY player today — SE-5538 shipped schema + API plumbing only,
   * with no image source populated. The card renders its initials fallback,
   * which is the expected state, not a defect.
   */
  imageUrl?: string | null;
  /**
   * SE-5492: the player's explicit position — distinct from `archetype`, which
   * is a ROLE archetype. Undefined when the player has no position source.
   */
  position?: string;
  /** SE-5492: total scout reports attached to the player (server-side count). */
  reportsCount?: number;
  /**
   * SE-5492: the latest scout report's overall score, or null when the player
   * has no scored report. Mirrors the preview drawer's verdict rule (SE-5494).
   */
  verdict?: string | null;
  /**
   * SE-6490: the backing kanban card's `updatedAt`, rendered as the footer's
   * "Updated 2d ago".
   */
  updatedAt?: string | null;
  /**
   * SE-6866: the backing card's `lastEditedByUserId`, completing that footer
   * with "· Hugo".
   * @remarks
   * The raw id, resolved to a name by this container (`useCardEditorName`)
   * rather than by the caller, so every card resolves through the one org
   * roster read Apollo dedupes. Null — no human editor, or an id the roster
   * cannot place — renders the time alone.
   */
  lastEditedByUserId?: string | null;
  /**
   * SE-4685: When true, the card body does NOT activate its own @dnd-kit drag.
   * Set by the rank-reorder sortable wrapper, which owns the drag node/id so
   * the card body stays inert and only the wrapper (with its grab handle)
   * initiates the within-column reorder drag.
   */
  isDragActivationDisabled?: boolean;
}

/**
 * Container component for kanban board player card with drag-and-drop functionality.
 * Manages card state, tag handling, hover effects, and dnd-kit integration.
 * @param root0 - Component props
 * @param root0.playerId - Unique identifier of the player
 * @param root0.playerName - Full name of the player
 * @param root0.rank - Column-scoped 1-based rank badge value (SE-4684)
 * @param root0.daysInStage - SE-5497: whole days in the current stage (pipeline-only)
 * @param root0.isStale - SE-5497: whether the card is over its stage's time target
 * @param root0.imageUrl - SE-5492: canonical player image; null today → initials fallback
 * @param root0.position - SE-5492: explicit position (not the role archetype)
 * @param root0.reportsCount - SE-5492: total scout reports attached to the player
 * @param root0.verdict - SE-5492: latest report's overall score, or null
 * @param root0.archetype - Player archetype (position and playing style)
 * @param root0.age - Player's age in years
 * @param root0.gpr - Gemini Performance Rating score
 * @param root0.contractValue - Formatted contract value string with currency
 * @param root0.contractMonths - Contract duration in months
 * @param root0.agentName - Name of player's agent
 * @param root0.teamName - Name of player's current team
 * @param root0.playerAgent - Additional agent information
 * @param root0.updatedAt - SE-6490: the card's last-updated timestamp, driving the footer
 * @param root0.lastEditedByUserId - SE-6866: the card's last human editor, resolved to a name here
 * @param root0.boardId - Unique identifier of the kanban board
 * @param root0.initialTags - Initial array of tag IDs associated with player
 * @param root0.onPress - Callback invoked when card is clicked
 * @param root0.onTagAdded - Callback invoked when a tag is added with tag details
 * @param root0.onAutoScroll - INERT. Its handler was passed to the View as
 *   `onDragging` and destructured there as `_onDragging` — never read — so
 *   nothing has called this since dnd-kit took over drag. The lift in
 *   SE-8019 made that provable and removed the handler. The prop is
 *   threaded from the board through a dozen components and is kept for that
 *   chain, not because anything invokes it.
 * @param root0.bioData - Additional biographical data for the player
 * @param root0.isOptimistic - Whether this card represents an optimistic UI update (triggers entrance animation)
 * @param root0.canEdit - Whether user can edit tags and interact with card (enables tag management)
 * @param root0.isDragActivationDisabled - SE-4685: when true the card body owns no drag node (the rank-reorder wrapper does)
 */
const KanbanPlayerCardContainer: React.FC<KanbanPlayerCardProps> = ({
  playerId,
  playerName,
  rank,
  daysInStage,
  isStale,
  imageUrl,
  position,
  reportsCount,
  verdict,
  archetype,
  age,
  gpr,
  contractValue,
  contractMonths,
  agentName,
  teamName,
  playerAgent,
  updatedAt,
  lastEditedByUserId,
  boardId,
  initialTags: _initialTags = [],
  isOptimistic,
  canEdit = true,
  isDragActivationDisabled = false,
  onPress,
  onTagAdded,
  // onDragStart,
  // onDragEnd,
  // `onAutoScroll` is deliberately NOT destructured: it is inert (see its note
  // on the props above) and naming it here would only invite a future reader to
  // wire it back up.
  bioData,
}) => {
  // SE-6866: the footer's "· Hugo". Resolved here rather than passed in so the
  // whole board shares one org-roster read (Apollo dedupes the identical
  // variables), and null-safe by construction — an unresolvable id renders the
  // time-only footer.
  const editorName = useCardEditorName(lastEditedByUserId);

  // Kanban card initialized
  const [isHovered, setIsHovered] = useState(false);
  const [showMoreTags, setShowMoreTags] = useState(false); // Start collapsed to prevent jumbling

  const {
    displayedTags,
    currentTagIds,
    expandedTagIndex,
    showAllTags,
    hasMoreTags,
    showTagSelector,
    setShowTagSelector,
    handlePress,
    handleAddTag,
    handleRemoveTag,
    handleTagsChanged,
    handleToggleTagExpansion,
    handleToggleShowAllTags,
  } = usePlayerCardBase({
    playerId,
    boardId,
    initialTags: [], // Let usePlayerCardBase handle tag state with optimistic updates
    canEdit,
    onPress,
    onTagAdded,
    onDragStart: undefined,
    onDragEnd: undefined,
    bioData,
  });

  const handleToggleShowMore = useCallback(() => {
    setShowMoreTags(prev => !prev);
  }, []);

  const handleHoverIn = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleHoverOut = useCallback(() => {
    setIsHovered(false);
  }, []);

  const draggableData = useMemo(
    () => ({
      id: playerId,
      playerName,
      contractValue,
      contractMonths,
      teamName,
      age,
      archetype,
      gpr,
    }),
    [
      playerId,
      playerName,
      contractValue,
      contractMonths,
      teamName,
      age,
      archetype,
      gpr,
    ]
  );

  const draggableStyle = useMemo(
    () => ({
      backgroundColor: isHovered ? KANBAN_COLORS_DARK.highlight : "transparent",
      borderRadius: RADIUS.md,
      // Add subtle shadow on hover using React Native format
      shadowColor: isHovered ? KANBAN_COLORS_DARK.primary : "transparent",
      shadowOffset: { width: 0, height: isHovered ? 8 : 0 },
      shadowOpacity: isHovered ? 0.15 : 0,
      shadowRadius: isHovered ? 16 : 0,
      // No transform here - let @dnd-kit handle all transforms
    }),
    [isHovered]
  );

  const activeStyle = useMemo(
    () => ({
      // SE-6554 (Figma 80:16392): the origin card is no longer hidden — it stays
      // in place, faded, until the drop commits. The opacity that does it lives
      // in the View next to the rest of the drag-node style (it has to win over
      // whatever is spread here, so keeping a second copy in this object could
      // only ever be a source of disagreement).
      //
      // No transform, background, or borders - let @dnd-kit and DragOverlay handle visuals
    }),
    []
  );

  const handlePreventDrag = useCallback(() => {
    // This can be used later if needed for tag interactions
  }, []);

  const handleDragAwareAddTag = useCallback(() => {
    handleAddTag();
  }, [handleAddTag]);

  const handleCloseTagSelector = useCallback(() => {
    setShowTagSelector(false);
  }, [setShowTagSelector]);

  // SE-4685: rank-reorder bindings arrive via context (RankSortableCard). When
  // present the card's own drag box becomes the whole-row sortable node — it
  // carries the sortable transform and activator listeners and renders the grab
  // handle inside itself, so handle and card move together. The card's own
  // cross-column draggable is disabled and registers a distinct id so it never
  // collides with the wrapper's `kanban-${playerId}` node.
  const sortable = useRankReorderSortable();
  const isDragDisabled = !!sortable || isDragActivationDisabled;

  const draggableId = isDragDisabled
    ? `kanban-inert-${playerId}`
    : `kanban-${playerId}`;
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: draggableId,
    disabled: isDragDisabled,
    data: {
      ...draggableData,
      // Marks the source for DndContext.
      source: "kanban",
    },
  });

  // Our controlled drag id, not dnd-kit's `isDragging`: dnd-kit resets that
  // synchronously on DragEnd — BEFORE React renders the optimistic cache update
  // — which makes the card flash at its old position. `activeDragId` clears
  // only when we call `setActive(null)`, which React batches with the
  // optimistic update, so by then the card is already in the new column.
  const activeDragId = useActiveDragId();
  const isBeingDragged = activeDragId === draggableId;

  // Cross-column draggable style.
  //
  // SE-6554 (Figma 80:16392, "Card · Dragging (origin)"): the origin card STAYS
  // IN PLACE at 40% opacity until the drop commits — it does not vanish. The
  // design's reason is legibility of the move: the faded card is where the
  // player came FROM, so a drag reads as a journey between two visible points
  // rather than a card that disappeared and a ghost that appeared.
  //
  // This was `opacity: 0` + `visibility: hidden`, to avoid showing the card
  // twice at once. Nothing about the layout changes by making it visible again
  // — `visibility: hidden` already reserved the row's full footprint, which is
  // what both the lane's own drop-gap arithmetic and
  // `resolveColumnInsertionIndex` measure — so this is a pixel change only, not
  // a geometry one.
  const draggableComputedStyle = useMemo(
    () => ({
      ...draggableStyle,
      ...(isBeingDragged ? activeStyle : {}),
      transform: transform ? CSS.Translate.toString(transform) : undefined,
      cursor: isDragDisabled ? "default" : isBeingDragged ? "grabbing" : "grab",
      opacity: isBeingDragged ? DRAGGING_ORIGIN_OPACITY : 1,
    }),
    [draggableStyle, isBeingDragged, activeStyle, transform, isDragDisabled]
  );

  // Rank-reorder style — the whole row translates in place with the sortable
  // transform and dims slightly while dragging (no overlay/hide).
  const sortableComputedStyle = useMemo(
    () => ({
      ...draggableStyle,
      transform: sortable?.transform
        ? CSS.Transform.toString(sortable.transform)
        : undefined,
      transition: sortable?.transition,
      cursor: "grab",
      // Same 40% the cross-column path leaves behind: with an overlay now
      // rendered for rank drags too, both drag types leave the identical faded
      // origin card, which is the single behaviour Figma 80:16392 describes for
      // "Card · Dragging (origin)".
      opacity: sortable?.isDragging ? DRAGGING_ORIGIN_OPACITY : 1,
    }),
    [draggableStyle, sortable]
  );

  return (
    <KanbanPlayerCardView
      playerId={playerId}
      playerName={playerName}
      rank={rank}
      daysInStage={daysInStage}
      isStale={isStale}
      imageUrl={imageUrl}
      position={position}
      reportsCount={reportsCount}
      verdict={verdict}
      archetype={archetype}
      age={age}
      gpr={gpr}
      contractValue={contractValue}
      contractMonths={contractMonths}
      agentName={agentName}
      teamName={teamName}
      playerAgent={playerAgent}
      updatedAt={updatedAt}
      editorName={editorName}
      displayedTags={displayedTags}
      currentTagIds={currentTagIds}
      expandedTagIndex={expandedTagIndex}
      showAllTags={showAllTags}
      hasMoreTags={hasMoreTags}
      isHovered={isHovered}
      showMoreTags={showMoreTags}
      showTagSelector={showTagSelector}
      isOptimistic={isOptimistic}
      boardId={boardId}
      dragBindings={
        sortable
          ? {
              ref: sortable.setNodeRef,
              ...sortable.listeners,
              ...sortable.attributes,
            }
          : { ref: setNodeRef, ...listeners, ...attributes }
      }
      dragStyle={sortable ? sortableComputedStyle : draggableComputedStyle}
      isRankSortable={!!sortable}
      onPress={handlePress}
      onAddTag={handleDragAwareAddTag}
      onRemoveTag={handleRemoveTag}
      onToggleTagExpansion={handleToggleTagExpansion}
      onToggleShowAllTags={handleToggleShowAllTags}
      onToggleShowMore={handleToggleShowMore}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onCloseTagSelector={handleCloseTagSelector}
      onTagsChanged={handleTagsChanged}
      onPreventDrag={handlePreventDrag}
      showRemoveButtons={canEdit}
      bioData={bioData}
    />
  );
};

KanbanPlayerCardContainer.displayName = "KanbanPlayerCardContainer";

/**
 * Memoized export to prevent unnecessary re-renders
 * @remarks
 * Uses React.memo with default shallow comparison. This prevents re-renders
 * when props don't change, but still allows re-renders when:
 * - Any prop changes (playerId, boardId, isOptimistic, etc.)
 * - Apollo cache updates trigger internal hook re-renders
 *
 * We don't use a custom comparison function because:
 * - Tag data comes from Apollo hooks inside the component
 * - Apollo triggers re-renders via internal state changes
 * - Custom comparison would block these necessary updates
 */
export default React.memo(KanbanPlayerCardContainer);
